import React, { useCallback, useMemo, useState } from 'react';
import { FacebookComment } from '@/types/fb-type';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { CalendarIcon, MessageSquareText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AssignDragGrip } from '@/pages/sale/components/assign-drag-grip';
import { useSaleAssignDraggable } from '@/pages/sale/hooks/use-sale-assign-draggable';
import { SaleAssignDragPayload } from '@/pages/sale/lib/sale-comment-drag';
import ReplyComponent from '../ReplyComponent';
import { OrderDetectionTag } from '../OrderDetectionTag';
import {
  CommentBubbleActions,
  commentBubbleClassName,
  formatCommentDate,
} from './comment-bubble-utils';

export type CommentBubbleProps = {
  data: FacebookComment;
  onPinToggle?: (id: string, pinned: boolean) => void;
  fmtDateTime?: (iso: string) => string;
  draggable?: boolean;
  dragPayload?: Record<string, unknown>;
  compact?: boolean;
  dense?: boolean;
};

export function CommentBubble({
  data,
  onPinToggle,
  fmtDateTime,
  draggable = true,
  dragPayload,
  compact = false,
  dense = false,
}: CommentBubbleProps) {
  const [isReplying, setIsReplying] = useState(false);
  const initial = data.fromName?.[0]?.toUpperCase() || 'U';
  const name = data.fromName ?? 'Unknown';
  const attachments = (data as FacebookComment & { attachments?: Array<{ id: string; previewUrl?: string; url?: string; name?: string }> }).attachments;

  const assignPayload = useMemo(
    () =>
      (dragPayload ?? {
        type: 'facebook_comment',
        id: data.id,
        message: data.message,
        fromName: data.fromName,
        fromId: data.fromId,
        createdTime: data.createdTime,
        productCode: (data as { taggedProduct?: unknown }).taggedProduct,
      }) as SaleAssignDragPayload,
    [data, dragPayload],
  );

  const {
    setNodeRef,
    isDragging,
    isSelected,
    draggableProps,
    handleRowClick,
    tapAssignEnabled,
    stateClassName,
  } = useSaleAssignDraggable(assignPayload, {
    disabled: !draggable,
    enableTapSelect: true,
  });

  const formatDate = useCallback(
    (iso: string) => formatCommentDate(iso, compact, fmtDateTime),
    [compact, fmtDateTime],
  );

  const isCompactLayout = compact || dense;

  return (
    <div
      ref={setNodeRef}
      {...draggableProps}
      onClick={(event) => handleRowClick(event)}
      className={cn(
        commentBubbleClassName({
          isPinned: data.isPinned,
          isNew: data.isNew,
          compact,
          dense,
        }),
        stateClassName,
        isDragging && 'scale-[0.98]',
        !tapAssignEnabled && 'select-none',
      )}
    >
      {data.isPinned && (
        <div className="absolute bottom-2 left-0 top-2 w-1 rounded-r bg-blue-500/80" />
      )}

      <AssignDragGrip
        size={isCompactLayout ? 'sm' : 'md'}
        className={!isCompactLayout ? 'group-hover:text-slate-600' : undefined}
      />

      <Avatar
        className={cn(
          'mt-0.5 shrink-0 border border-gray-200',
          dense ? 'h-6 w-6' : compact ? 'h-7 w-7' : 'h-8 w-8 sm:h-9 sm:w-9',
        )}
      >
        {data.profileImage ? (
          <AvatarImage src={data.profileImage} alt={name} />
        ) : (
          <AvatarFallback className="bg-gray-100 text-xs font-medium text-gray-600 sm:text-sm">
            {initial}
          </AvatarFallback>
        )}
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-1 sm:gap-2">
          <div className="min-w-0 flex-1">
            {compact ? (
              <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                <span className="truncate text-xs font-semibold text-gray-900">{name}</span>
                <span className="shrink-0 text-[10px] text-gray-400">
                  {formatDate(data.createdTime || '')}
                </span>
                {data.isPinned && (
                  <Badge className="h-4 shrink-0 bg-blue-600 text-[9px] text-white">
                    Pinned
                  </Badge>
                )}
                {data.isNew && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-600" />
                    New
                  </span>
                )}
                {data.replyCount ? (
                  <span className="inline-flex shrink-0 items-center gap-0.5 text-[10px] text-gray-500">
                    <MessageSquareText className="h-3 w-3" />
                    {data.replyCount}
                  </span>
                ) : null}
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-1 sm:gap-1.5">
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
                    {name}
                  </span>

                  {data.isPinned && (
                    <Badge className="h-4 shrink-0 bg-blue-600 text-[9px] text-white sm:h-5 sm:text-[10px]">
                      Pinned
                    </Badge>
                  )}
                  {data.isNew && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700 sm:px-2 sm:text-[10px]">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-600" />
                      New
                    </span>
                  )}
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-gray-500 sm:gap-2 sm:text-[11px]">
                  <div className="inline-flex min-w-0 items-center gap-1 text-slate-500">
                    <CalendarIcon className="hidden h-3.5 w-3.5 shrink-0 sm:inline sm:h-4 sm:w-4" />
                    <span className="truncate">{formatDate(data.createdTime || '')}</span>
                  </div>

                  {data.replyCount ? (
                    <span className="inline-flex shrink-0 items-center gap-1">
                      <MessageSquareText className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                      {data.replyCount} repl{data.replyCount > 1 ? 'ies' : 'y'}
                    </span>
                  ) : null}
                </div>
              </>
            )}
          </div>

          <CommentBubbleActions
            isPinned={!!data.isPinned}
            showDragHint={!compact}
            tapAssignEnabled={tapAssignEnabled}
            isSelected={isSelected}
            onPinToggle={
              onPinToggle ? () => onPinToggle(data.id, !data.isPinned) : undefined
            }
            onReply={() => setIsReplying(true)}
          />
        </div>

        {isReplying && data.id && (
          <ReplyComponent
            authorName={data.fromName}
            commentId={data.id}
            onClose={() => setIsReplying(false)}
            compact={compact || dense}
          />
        )}

        <div
          className={cn(
            'block w-full min-w-0 max-w-full leading-snug text-gray-800',
            dense
              ? 'mt-0.5 text-[11px] leading-snug'
              : compact
                ? 'mt-1 rounded-md bg-gray-50/90 px-1.5 py-0.5 text-xs leading-snug'
                : 'mt-2 rounded-md bg-gray-50 px-2 py-1.5 text-xs shadow-inner sm:px-2.5 sm:py-2 sm:text-[13px]',
            data.isPinned && !compact && !dense && 'bg-white/80',
          )}
        >
          <p className="whitespace-pre-wrap break-words">
            {data.message || <span className="italic text-gray-400">No message</span>}
          </p>

          {data.orderByComment && (
            <div className={cn('w-full min-w-0 overflow-hidden', dense ? 'mt-1' : compact ? 'mt-1' : 'mt-2')}>
              <OrderDetectionTag data={data.orderByComment} compact={compact || dense} dense={dense} />
            </div>
          )}

          {!!attachments?.length && (
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {attachments.slice(0, compact ? 4 : 6).map((attachment) => (
                <div
                  key={attachment.id}
                  className="overflow-hidden rounded-lg border border-gray-200 bg-white"
                >
                  <img
                    src={attachment.previewUrl || attachment.url}
                    alt={attachment.name || 'attachment'}
                    className={cn(
                      'w-full object-cover',
                      compact ? 'h-20 sm:h-24' : 'h-24',
                    )}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
